import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Eye } from 'lucide-react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Badge } from '../../../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../../../components/ui/table';
import dashboardMock from './projectDashboardMock.json';
import { fetchProjectSpoolDashboard } from '../../../../../redux/Spoolsbase/actions';
import CommonLoader from '../../../../../components/Loaders/loader';

type ChangeTone = 'up' | 'down' | 'neutral';
type KpiAccent = 'accent' | 'teal' | 'warn' | 'default';
type TypeTone = 'active' | 'warn' | 'pending';
type StatusTone = 'pass' | 'warn' | 'pending' | 'draft';
type ActivityDot = 'green' | 'orange' | 'red' | 'blue';
type WitnessWhenTone = 'accent' | 'warn' | 'muted';

interface ProjectDashboardMock {
  pageHeader: {
    title: string;
    exportLabel: string;
    quickEntryLabel: string;
  };
  productManagerNote: {
    title: string;
    emphasis: string;
    body: string;
  };
  kpis: Array<{
    id: string;
    label: string;
    value: string;
    valueSecondary: string | null;
    change: string;
    changeTone: ChangeTone;
    accent: KpiAccent;
  }>;
  spoolFabrication: {
    title: string;
    viewAllLabel: string;
    columns: string[];
    rows: Array<{
      rowKey?: string;
      spool: string;
      type: { label: string; tone: TypeTone };
      welds: string;
      ndt: string;
      coating: 'complete' | 'pending' | '—';
      fat: string;
      status: { label: string; tone: StatusTone };
    }>;
  };
  recentActivity: {
    title: string;
    items: Array<{
      dot: ActivityDot;
      title: string;
      description: string;
      meta: string;
    }>;
  };
  witnessPoints: {
    title: string;
    items: Array<{
      title: string;
      subtitle: string;
      when: string;
      whenTone: WitnessWhenTone;
    }>;
  };
}

export interface ProjectDashboardProps {
  projectId: string | number;
  /** When set, dashboard KPIs and rows are limited to this pipe (matches backend `pipe_id`). */
  pipeId?: string | number | null;
}

const fallbackData = dashboardMock as ProjectDashboardMock;

const kpiAccentClass: Record<KpiAccent, string> = {
  accent: 'border-l-[4px] border-l-[#E8520A]',
  teal: 'border-l-[4px] border-l-[#1A7A6E]',
  warn: 'border-l-[4px] border-l-[#F59E0B]',
  default: 'border-l-[4px] border-l-transparent',
};

const changeToneClass: Record<ChangeTone, string> = {
  up: 'text-[#1A7A6E]',
  down: 'text-red-600',
  neutral: 'text-slate-600',
};

const typeBadgeClass: Record<TypeTone, string> = {
  active: 'border-transparent bg-emerald-50 text-emerald-800',
  warn: 'border-transparent bg-amber-50 text-amber-900',
  pending: 'border-transparent bg-blue-50 text-blue-800',
};

const statusBadgeClass: Record<StatusTone, string> = {
  pass: 'border-transparent bg-green-50 text-green-800',
  warn: 'border-transparent bg-amber-50 text-amber-900',
  pending: 'border-transparent bg-blue-50 text-blue-800',
  draft: 'border-transparent bg-slate-100 text-slate-700',
};

const activityDotClass: Record<ActivityDot, string> = {
  green: 'bg-green-500',
  orange: 'bg-orange-500',
  red: 'bg-red-500',
  blue: 'bg-blue-500',
};

const witnessWhenClass: Record<WitnessWhenTone, string> = {
  accent: 'text-[#E8520A]',
  warn: 'text-amber-600',
  muted: 'text-slate-500',
};

function CoatingCell({ value }: { value: 'complete' | 'pending' | '—' }) {
  if (value === 'complete') {
    return <span aria-label="Complete">✅</span>;
  }
  if (value === 'pending') {
    return <span aria-label="Pending">⏳</span>;
  }
  return <span className="text-slate-400">—</span>;
}

export const ProjectDashboard: React.FC<ProjectDashboardProps> = ({ projectId, pipeId }) => {
  const dispatch = useDispatch();
  const dashboard = useSelector((state: { spoolsbase?: { projectSpoolDashboard?: ProjectDashboardMock | null } }) =>
    state.spoolsbase?.projectSpoolDashboard != null ? state.spoolsbase.projectSpoolDashboard : null
  );
  const loading = useSelector(
    (state: { spoolsbase?: { projectSpoolDashboardLoading?: boolean } }) =>
      Boolean(state.spoolsbase?.projectSpoolDashboardLoading)
  );
  const loadError = useSelector(
    (state: { spoolsbase?: { projectSpoolDashboardError?: string | null } }) =>
      state.spoolsbase?.projectSpoolDashboardError ?? null
  );

  const fetchEnabled = projectId != null && projectId !== '';

  useEffect(() => {
    if (!fetchEnabled) return;
    dispatch(fetchProjectSpoolDashboard({ projectId, pipeId: pipeId ?? undefined }));
  }, [dispatch, projectId, pipeId, fetchEnabled]);

  const fromApi = Boolean(dashboard && typeof dashboard === 'object' && Array.isArray(dashboard.kpis));

  const data = useMemo(() => {
    if (fromApi) {
      return dashboard as ProjectDashboardMock;
    }
    return fallbackData;
  }, [dashboard, fromApi]);

  /** Live API in flight, or waiting first paint before saga sets loading — do not flash mock data. */
  const showDashboardBody =
    !fetchEnabled || fromApi || (!loading && loadError != null && String(loadError).trim() !== '');

  const { pageHeader, productManagerNote, kpis, spoolFabrication, recentActivity, witnessPoints } = data;

  return (
    <div className="mb-8 space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">{'Dashboard'}</h1>
          {fetchEnabled && !showDashboardBody ? (
            <p className="mt-1 text-xs text-slate-500">Loading dashboard…</p>
          ) : null}
          {!loading && loadError && !fromApi ? (
            <p className="mt-1 text-xs text-amber-700">Could not load live data — showing sample layout. ({loadError})</p>
          ) : null}
        </div>
        {/* <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            {pageHeader.exportLabel}
          </Button>
          <Button type="button" className="gap-2 bg-[#E8520A] hover:bg-[#cf4a09]">
            <Plus className="h-4 w-4" />
            {pageHeader.quickEntryLabel}
          </Button>
        </div> */}
      </div>

      {/* <div
        className="rounded-md border border-amber-200 border-l-4 border-l-amber-500 bg-[#FFFDE7] px-4 py-3 text-xs text-[#5D4037]"
        role="note"
      >
        <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-amber-700">
          {productManagerNote.title}
        </div>
        <p>
          <strong className="text-orange-700">{productManagerNote.emphasis}</strong> {productManagerNote.body}
        </p>
      </div> */}

      {!showDashboardBody ? (
        <div
          className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-lg border border-slate-200 bg-slate-50/80 py-12"
          role="status"
          aria-busy="true"
          aria-label="Loading dashboard"
        >
          <CommonLoader />
          <p className="text-sm text-slate-600">Loading dashboard…</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {kpis.map((kpi) => (
              <Card
                key={kpi.id}
                className={`border border-slate-200 shadow-sm ${kpiAccentClass[kpi.accent]}`}
              >
                <CardContent className="p-4">
                  <div className="text-xs font-medium uppercase tracking-wide text-slate-500">{kpi.label}</div>
                  <div className="mt-1 text-3xl font-bold leading-none text-[#0F1D2E]">
                    {kpi.value}
                    {kpi.valueSecondary ? (
                      <span className="text-base font-normal text-slate-500">{kpi.valueSecondary}</span>
                    ) : null}
                  </div>
                  <div className={`mt-2 text-xs font-medium ${changeToneClass[kpi.changeTone]}`}>{kpi.change}</div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <Card className="border border-slate-200 shadow-sm lg:col-span-2">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                <h3 className="text-base font-semibold text-slate-900">{spoolFabrication.title}</h3>
                <Button type="button" variant="outline" size="sm" className="h-8 text-xs">
                  {spoolFabrication.viewAllLabel}
                </Button>
              </div>
              <Table containerClassName="relative w-full overflow-x-auto rounded-b-xl">
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    {spoolFabrication.columns.map((col) => (
                      <TableHead key={col} className="whitespace-nowrap text-xs font-semibold text-slate-600">
                        {col}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spoolFabrication.rows.map((row) => (
                    <TableRow key={row.rowKey ?? row.spool}>
                      <TableCell className="font-mono text-sm font-semibold text-slate-900">{row.spool}</TableCell>
                      <TableCell>
                        <Badge className={typeBadgeClass[row.type.tone]}>{row.type.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm">{row.welds}</TableCell>
                      <TableCell className="text-sm">{row.ndt}</TableCell>
                      <TableCell className="text-sm">
                        <CoatingCell value={row.coating} />
                      </TableCell>
                      <TableCell className="text-sm text-slate-700">{row.fat}</TableCell>
                      <TableCell>
                        <Badge className={statusBadgeClass[row.status.tone]}>{row.status.label}</Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>

            <div className="flex flex-col gap-4">
              <Card className="border border-slate-200 shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="text-base font-semibold text-slate-900">{recentActivity.title}</h3>
                </div>
                <CardContent className="space-y-0 p-4 pt-2">
                  <div className="relative space-y-4 before:absolute before:left-[7px] before:top-2 before:h-[calc(100%-12px)] before:w-px before:bg-slate-200">
                    {recentActivity.items.map((item, idx) => (
                      <div key={idx} className="relative flex gap-3 pl-1">
                        <div
                          className={`relative z-[1] mt-1.5 h-3.5 w-3.5 shrink-0 rounded-full ${activityDotClass[item.dot]}`}
                        />
                        <div className="min-w-0 flex-1 pb-1">
                          <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                          <div className="mt-0.5 text-xs text-slate-600">{item.description}</div>
                          <div className="mt-1 text-[11px] text-slate-500">{item.meta}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border border-slate-200 shadow-sm">
                <div className="border-b border-slate-100 px-4 py-3">
                  <h3 className="text-base font-semibold text-slate-900">{witnessPoints.title}</h3>
                </div>
                <CardContent className="divide-y divide-slate-100 p-3">
                  {witnessPoints.items.map((wp, idx) => (
                    <div key={idx} className="flex items-center gap-3 py-2 first:pt-0 last:pb-0">
                      <Eye className="h-5 w-5 shrink-0 text-slate-500" aria-hidden />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold text-slate-900">{wp.title}</div>
                        <div className="text-[11px] text-slate-500">{wp.subtitle}</div>
                      </div>
                      <div className={`shrink-0 text-[11px] font-semibold ${witnessWhenClass[wp.whenTone]}`}>
                        {wp.when}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
